/**
 * useTenantName — retorna o nome do tenant atual com cache em localStorage
 * Evita que cada página precise fazer GET /tenants/:id só para exibir o nome
 */
import { useState, useEffect } from 'react';
import api from '../services/api';

const CACHE_KEY = 'tenant_names'; // { tenantId: name }
const CACHE_TTL = 30 * 60 * 1000; // 30 min

function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const { data, at } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL) { localStorage.removeItem(CACHE_KEY); return {}; }
    return data || {};
  } catch { return {}; }
}

function setCache(tenantId, name) {
  try {
    const existing = getCache();
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: { ...existing, [tenantId]: name }, at: Date.now() }));
  } catch {}
}

export function useTenantName(tenantId) {
  // Lista de segmentos que não são tenantIds reais
  const INVALID = ['dashboard', 'marketplace', 'analytics', 'settings', 'tenants', 'agents', 'task-templates', null, undefined];

  const [name, setName] = useState(() => {
    if (!tenantId || INVALID.includes(tenantId)) return null;
    const cached = getCache()[tenantId];
    if (cached) return cached;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.tenantId === tenantId && user.organizationName) return user.organizationName;
    } catch {}
    return null;
  });

  useEffect(() => {
    if (!tenantId || INVALID.includes(tenantId)) return;
    const cached = getCache()[tenantId];
    if (cached) { setName(cached); return; }

    api.get(`/tenants/${tenantId}`)
      .then(res => {
        const n = res.data?.name;
        if (n) { setCache(tenantId, n); setName(n); }
      })
      .catch(() => {});
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return name;
}
